import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { first, Subscription } from 'rxjs';
import { ActionConfig, ActionDto, ActionModal, BitbucketService, MachineId, ModalService, OaiboxType, StompService, TenantId, WebAppCommonModule } from "@allbesmart/web-app-common";
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from "@angular/forms";
import { TenantManagementService } from "../../services/tenant-management.service";
import { WS_SUFFIX } from "../../external/ws";

@Component({
    selector: 'oaibox-gnb-upgrade-config-action-modal',
    templateUrl: './gnb-upgrade-config-action-modal.component.html',
    styleUrls: ['./gnb-upgrade-config-action-modal.component.css'],
    imports: [WebAppCommonModule, ReactiveFormsModule]
})
export class GnbUpgradeConfigActionModalComponent implements OnInit, OnDestroy, ActionModal {

  @Input({required: true}) public actionConfig!: ActionConfig;
  @Input({required: true}) public sendAction!: (action: GNBUpgradeDto) => void;
  @Input({required: true}) public actionTenantId!: TenantId;
  @Input({required: true}) public actionMachineId!: MachineId;

  protected selectVersionForm: FormGroup<{ selectedVersion: FormControl<string | null> }>;
  private selectVersionChangesSubscription: Subscription;

  private knownVersions: Set<string> = new Set();
  private knownVersionsSubscription?: Subscription;
  private currentVersionSubscription?: Subscription;

  public showMatches = false;
  protected matchedVersions: string[] = [];

  constructor(
    private tenantManagementService: TenantManagementService,
    private bitbucketService: BitbucketService,
    private modalService: ModalService,
    private stompService: StompService,
  ) {
    this.selectVersionForm = new FormGroup({
      selectedVersion: new FormControl<string | null>(null, [Validators.required]),
    });

    // Filter Shown Results on changes
    this.selectVersionChangesSubscription = this.selectVersionForm.valueChanges
      .subscribe(changes => this.filterShownVersions());
  }

  ngOnInit(): void {
    if (!this.actionTenantId) throw new Error('actionTenantId not initialised.');
    if (!this.actionMachineId) throw new Error('actionMachineId not initialised.');

    const machine = this.tenantManagementService.getTenantByTenantId(this.actionTenantId)?.registeredMachines
      .find(machine => machine.id === this.actionMachineId);

    if (machine) {
      // Fetch Known Versions
      this.knownVersionsSubscription = ([OaiboxType.OAIBOX_DU, OaiboxType.OAIBOX_CU].includes(machine.productDetails.oaiboxType) ?
        this.bitbucketService.getOaiboxORanTags$() : this.bitbucketService.getOaiboxMonolithicTags$())
        .pipe(first()).subscribe(tags => {
          tags.forEach(tag => this.knownVersions.add(tag));
          this.filterShownVersions();
        });
    }

    this.currentVersionSubscription = this.stompService.subscribeTopic<{
        branch: string
      }>(`${this.actionTenantId}.${this.actionMachineId}.${WS_SUFFIX.GNB_CONFIGURATION}`)
      .pipe(first())
      .subscribe(configuration => {
        if (!this.selectVersionForm.value.selectedVersion) this.selectVersionForm.controls.selectedVersion.patchValue(configuration.branch);
      });
  }

  protected filterShownVersions(): void {
    this.matchedVersions = [...this.knownVersions]
      .filter(version => version.includes(this.selectVersionForm.value.selectedVersion ?? ''));
  }

  protected selectVersion(version: string) {
    this.selectVersionForm.patchValue({selectedVersion: version});
    this.filterShownVersions();
    this.showMatches = false;
  }

  protected confirmAction(): void {
    if (this.actionConfig && this.selectVersionForm.value.selectedVersion) {
      this.sendAction({
        type: this.actionConfig.type,
        command: this.actionConfig.command,
        configuration: {branch: this.selectVersionForm.value.selectedVersion}
      });
      this.modalService.popModal();
    }
  };

  protected cancel(): void {
    this.modalService.popModal();
  }

  ngOnDestroy(): void {
    this.knownVersionsSubscription?.unsubscribe();
    this.selectVersionChangesSubscription.unsubscribe();
    this.currentVersionSubscription?.unsubscribe();
  }

}

export interface GNBUpgradeDto extends ActionDto {
  configuration?: { branch: string; }
}
